import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../../config/api'

/**
 * Rule ↔ Risk Register links panel — S10 of the production-readiness
 * tracker.
 *
 * Shows which risks a given rule is currently mapped to in the risk
 * register, and lets an authoring CO add or remove links inline
 * without leaving the rule builder.
 *
 * Backend contract (see backend/src/routes/risk-items.js):
 *   GET    /api/risk-items/by-control?type=rule&id=<ruleId>
 *            → [{ id, title, status, typology_ref, inherent_score,
 *                rationale, linked_at }, ...]
 *   GET    /api/risk-items?q=<term>
 *            → { data: [{ id, title, status, ... }, ...] }
 *   POST   /api/risk-items/:riskId/links
 *            body { control_type: 'rule', control_id, rationale? }
 *            409 on duplicate
 *   DELETE /api/risk-items/:riskId/links/:linkId
 *
 * Mount on any rule-authoring surface — renders nothing interactive
 * until `ruleId` has a value (a brand-new rule has to be saved once
 * before it can be linked).
 */

const STATUS_STYLE = {
  open:       { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  mitigating: { color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  accepted:   { color: '#1e40af', bg: '#dbeafe', border: '#bfdbfe' },
  closed:     { color: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
}

function StatusPill({ status }) {
  const style = STATUS_STYLE[status] || { color: '#475569', bg: '#f1f5f9', border: '#e2e8f0' }
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border"
      style={{ color: style.color, background: style.bg, borderColor: style.border }}
    >
      {status || 'unknown'}
    </span>
  )
}

export default function RuleRiskLinksPanel({ ruleId }) {
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [linking, setLinking] = useState(false)
  const debounceRef = useRef(null)

  const fetchLinks = useCallback(async () => {
    if (!ruleId) { setLinks([]); return }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ type: 'rule', id: ruleId })
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/by-control?${params.toString()}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setLinks(Array.isArray(json.data) ? json.data : [])
    } catch (e) {
      setError(e.message || 'Failed to load linked risks')
      setLinks([])
    } finally {
      setLoading(false)
    }
  }, [ruleId])

  useEffect(() => { fetchLinks() }, [fetchLinks])

  const runSearch = useCallback(async (term) => {
    const q = String(term || '').trim()
    if (q.length < 2) { setSearchResults([]); return }
    setSearching(true)
    try {
      const params = new URLSearchParams({ q, page_size: '10' })
      const res = await authFetch(getLocalApiUrl(`/api/risk-items?${params.toString()}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      const existing = new Set(links.map(l => l.id))
      setSearchResults((Array.isArray(json.data) ? json.data : []).filter(r => !existing.has(r.id)))
    } catch (_) {
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, [links])

  useEffect(() => {
    if (!searchOpen) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(searchTerm), 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchTerm, searchOpen, runSearch])

  const addLink = async (riskId) => {
    if (!ruleId || linking) return
    setLinking(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(riskId)}/links`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ control_type: 'rule', control_id: ruleId }),
      })
      const json = await res.json().catch(() => ({}))
      if (res.status === 409) {
        // Already linked — refresh and move on.
      } else if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setSearchOpen(false)
      setSearchTerm('')
      setSearchResults([])
      await fetchLinks()
    } catch (e) {
      setError(e.message || 'Failed to link risk')
    } finally {
      setLinking(false)
    }
  }

  const removeLink = async (riskId) => {
    if (!ruleId) return
    // by-control returns the risk row with a `link_id` on the join record.
    // The detail endpoint fills link_id on the controls array; by-control
    // uses `linked_at` and the risk id. We resolve the link_id by hitting
    // the detail endpoint for that risk, since DELETE needs link_id.
    try {
      const res = await authFetch(getLocalApiUrl(`/api/risk-items/${encodeURIComponent(riskId)}`))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      const controls = Array.isArray(json.data?.controls) ? json.data.controls : []
      const match = controls.find(c => c.control_type === 'rule' && String(c.control_id) === String(ruleId))
      if (!match) {
        // Nothing to remove — just refresh.
        await fetchLinks()
        return
      }
      const del = await authFetch(
        getLocalApiUrl(`/api/risk-items/${encodeURIComponent(riskId)}/links/${encodeURIComponent(match.id)}`),
        { method: 'DELETE' }
      )
      if (!del.ok) {
        const dj = await del.json().catch(() => ({}))
        throw new Error(dj.error || `HTTP ${del.status}`)
      }
      await fetchLinks()
    } catch (e) {
      setError(e.message || 'Failed to remove link')
    }
  }

  if (!ruleId) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
        Save the rule once to enable linking it to risks in the register.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">Linked risks</div>
          <div className="text-[11px] text-slate-500">
            Which risk register items does this rule mitigate? (S10)
          </div>
        </div>
        <button
          type="button"
          className="text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
          onClick={() => setSearchOpen((v) => !v)}
        >
          {searchOpen ? 'Cancel' : '+ Link to risk'}
        </button>
      </div>

      {error && (
        <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}

      {loading && <div className="text-xs text-slate-500">Loading linked risks…</div>}

      {!loading && links.length === 0 && !searchOpen && (
        <div className="text-xs text-slate-400 italic">No risks linked yet.</div>
      )}

      {!loading && links.length > 0 && (
        <ul className="space-y-1.5">
          {links.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[13px] text-slate-800 truncate">
                  <span className="font-mono text-[11px] text-slate-500 truncate">{r.id}</span>
                  <span className="truncate">{r.title}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusPill status={r.status} />
                  {r.typology_ref && (
                    <span className="text-[10px] text-slate-500">{r.typology_ref}</span>
                  )}
                  {r.inherent_score != null && (
                    <span className="text-[10px] text-slate-500">score {r.inherent_score}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="text-[11px] text-slate-500 hover:text-red-600"
                onClick={() => removeLink(r.id)}
                title="Remove link"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {searchOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <input
            type="text"
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search risk titles or descriptions (min 2 chars)…"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searching && <div className="text-[11px] text-slate-500 mt-1">Searching…</div>}
          {!searching && searchTerm.trim().length >= 2 && searchResults.length === 0 && (
            <div className="text-[11px] text-slate-400 italic mt-2">No matches.</div>
          )}
          {searchResults.length > 0 && (
            <ul className="mt-2 space-y-1 max-h-60 overflow-auto">
              {searchResults.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => addLink(r.id)}
                    disabled={linking}
                    className="w-full text-left rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50 px-2 py-1.5 disabled:opacity-50"
                  >
                    <div className="flex items-center gap-2 text-[13px] text-slate-800">
                      <span className="font-mono text-[11px] text-slate-500">{r.id}</span>
                      <span className="truncate">{r.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusPill status={r.status} />
                      {r.typology_ref && (
                        <span className="text-[10px] text-slate-500">{r.typology_ref}</span>
                      )}
                      {r.inherent_score != null && (
                        <span className="text-[10px] text-slate-500">score {r.inherent_score}</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
